import axios from 'axios'
import isEmpty from 'lodash/isEmpty'
import { loadStripe } from '@stripe/stripe-js'
import billingConfig from '~/plugins/config/billingParameters'
import planSettings from '~/plugins/config/planSettings'
import Bugsnag from '@bugsnag/js'

export const getDefaultCurrencyCode = () => {
  return billingConfig.default_currency
}

export const getplanSettings = () => {
  return planSettings
}
//
// Return setting value for plan
export const getSettingForPlan = ({ setting, planName }) => {
  // eslint-disable-next-line no-prototype-builtins
  if (!planSettings.hasOwnProperty(planName) || !planSettings[planName].hasOwnProperty(setting)) {
    return 'error'
  }
  return planSettings[planName][setting]
}
/**
 * Get user subscription details
 */
// TODO : This is a hack, we'll optimise with new user fields
export const getSubscriptionDetails = (user) => {
  if (isEmpty(user.eventPlanSubscriptions)) {
    return null
  }
  return {
    planName: user.eventPlanSubscriptions[0].name,
    // eslint-disable-next-line eqeqeq
    isActive: user.eventPlanSubscriptions[0].stripe_status == 'active'
  }
}
/**
 * At the moment only users based in the UK will be charged tax
 *
 * @param countryName
 * @returns {*}
 */
export const isTaxable = (countryName) => {
  return billingConfig.taxable_countries.includes(countryName)
}
/**
 * Only one tax rate to consider at the moment
 *
 * @returns number (tax rate percent)
 */
export const getTaxRate = () => {
  return billingConfig.tax_rate
}
/**
 * Get all salesPlans and salesPlanPrices
 *
 * @returns {Promise<{notification: null, plans: any}|{notification: {type: string, message: string}, plans: null}>}
 */
export const getAllSalesPlans = async () => {
  try {
    const resp = await axios.get('/salesplan')
    return {
      plans: resp.data,
      notification: null
    }
  } catch (err) {
    Bugsnag.notify(err)
    return {
      plans: null,
      notification: {
        type: 'error',
        message: `Error getting all sales plans : ${err.message}`
      }
    }
  }
}
/**
 * Load strip javascript and request object based on Stripe Key
 *
 * @returns {Promise<void>}
 */
export const getStripeObject = async () => {
  return await loadStripe(process.env.MIX_STRIPE_KEY)
}
/**
 * Get on file payment methods for current user
 *
 * @returns {Promise<{notification: null, paymentMethods: any}|{notification: {type: string, message: string}, paymentMethods: null}>}
 */
export const getUserPaymentMethods = async () => {
  try {
    const resp = await axios.get('user/payment-methods')
    return {
      paymentMethods: resp.data,
      notification: null
    }
  } catch (err) {
    Bugsnag.notify(err)
    return {
      paymentMethods: null,
      notification: {
        type: 'warning',
        message: `Error getting existing payment messages : ${err.message}`
      }
    }
  }
}
/**
 * Get Stripe payment intent token
 *
 * @returns {Promise<{notification: {type: string, message: string}, intentToken: null}|{notification: null, intentToken: any}>}
 */
export const createStripeSetupIntent = async () => {
  try {
    const resp = await axios.get('/user/setup-intent')
    return {
      intentToken: resp.data,
      notification: null
    }
  } catch (err) {
    Bugsnag.notify(err)
    return {
      intentToken: null,
      notification: {
        type: 'error',
        message: `Error retrieve Stripe setup intent : ${err.message}`
      }
    }
  }
}
/**
 * Add new payment method to Stripe
 * @param stripe
 * @param token
 * @param card
 * @param name
 * @returns {Promise<void>}
 */
export const addStripePaymentMethod = async ({ stripe, token, card, name }) => {
  return stripe.confirmCardSetup(
    token.client_secret, {
      payment_method: {
        card: card,
        billing_details: {
          name: name
        }
      }
    }
  )
}
/**
 * The method is returned by the stripe.confirmCardSetup
 *
 * @param method
 * @param options
 * @returns {Promise<{notification: {type: string, message: string}}|{notification: null}>}
 */
export const saveStripePaymentMethod = async ({ method, options }) => {
  try {
    await axios.post('/user/payments', {
      payment_method: method,
      customer_options: options
    })
    return {
      notification: null
    }
  } catch (err) {
    Bugsnag.notify(err)
    return {
      notification: {
        type: 'error',
        message: `Error saving payment intent : ${err.message}`
      }
    }
  }
}
/**
 * Create subscription for Event Owner
 *
 * @param planId
 * @param paymentMethodId
 * @param planType
 * @returns {Promise<{notification: {type: string, message: string}, subscriptionsData:null}|{notification: null, subscriptionsData: any}>}
 */
export const updateSubscription = async ({ planId, paymentMethodId, planType, currencyId, subscriptionType }) => {
  try {
    const resp = await axios.put('/user/subscription', {
      plan: planId,
      payment: paymentMethodId,
      planType: planType,
      currencyId: currencyId,
      subscriptionType: subscriptionType
    })
    console.log(' Reply => ', resp)
    return {
      subscriptionsData: resp.data.subscription_data,
      notification: null
    }
  } catch (err) {
    if (err.response.data) {
      return {
        subscriptionsData: err.response.data,
        confirm: true,
        notification: null
      }
    } else {
      Bugsnag.notify(err)
      return {
        notification: {
          type: 'error',
          message: `Error creating subscription : ${err.message}`
        },
        subscriptionsData: null
      }
    }
  }
}
export const redirectToBillingPortal = async (returnAddress = null) => {
  try {
    await axios.get('/billing-portal', {
      returnEndpoint: returnAddress
    })
    return {
      notification: null
    }
  } catch (err) {
    Bugsnag.notify(err)
    return {
      notification: {
        type: 'Warning',
        message: `Your billing portal is not available : ${err.message}`
      }
    }
  }
}
/**
 * The charge amount needs to be in the "smallest currency unit"
 * For all the currencies we deal with, this is pennies or cents - i.e. 0.01
 * This routine will just convert the amount passed to an integer (assumed to be fixed to 2 decimal places)
 * @param value
 * @returns {number}
 */
const getPaymentAmount = (value) => {
  const amount = Number(value)
  return isNaN(amount) ? 0 : Math.round(amount * 100)
}
/**
 *
 * @param paymentMethod - payment method token
 * @param value
 * @param currencyIsoCode - e.g. gbp
 * @param options - stripe customer data
 * @param eventTicketId
 * @returns
 */
export const purchaseAttendeeTicket = async ({ paymentIntent, value, currencyIsoCode, options, eventTicketId, salesPrice, ticketTax, currencyId }) => {
  try {
    // Convert value to lowest currency unit
    const paymentAmount = getPaymentAmount(value)
    if (paymentAmount === 0) {
      throw new Error('Invalid payment amount!') // No way should this happen
    }
    const resp = await axios.post('/user/purchase-attendee-ticket', {
      paymentIntent: paymentIntent,
      value: paymentAmount,
      currency: currencyIsoCode.toLowerCase(),
      salesPrice: salesPrice,
      ticketTax: ticketTax,
      currencyId: currencyId,
      options: options,
      eventTicketId: eventTicketId
    })
    // status = true  - payment succeeded and ticket paid
    // status = false - ticket could be allocated, payment not taken
    // No status object, we probably got an {"error":{}} response from the backed - this means that stripe could not process the card
    //
    // eslint-disable-next-line no-prototype-builtins
    if (resp.data.hasOwnProperty('status')) {
      if (resp.data.status) {
        // Payment processed successfully
        return {
          paymentSuccess: true,
          notification: null
        }
      } else {
        // Payment not taken - ticket could not be allocated
        return {
          paymentSuccess: false,
          notification: {
            type: 'Error',
            message: 'There was a problem assigning your ticket, your card has not been charged - please contact support'
          }
        }
      }
    }
    // Looks like we got a 200 response with 'error'
    return {
      paymentSuccess: false,
      cardCouldNotBeProcessed: true,
      notification: null
    }
  } catch (err) {
    Bugsnag.notify(err)
    return {
      paymentSuccess: false,
      notification: {
        type: 'Warning',
        message: `Error processing payment : ${err.message}`
      }
    }
  }
}

/**
 * Get Stripe payment intent token
 *
 * @returns {Promise<{notification: {type: string, message: string}, intentToken: null}|{notification: null, intentToken: any}>}
 */
export const createPaymentIntent = async (data) => {
  try {
    const resp = await axios.post('/user/create-payment-intent', data)
    return {
      paymentIntent: resp.data
    }
  } catch (err) {
    Bugsnag.notify(err)
    return {
      intentToken: null,
      notification: {
        type: 'error',
        message: `Error retrieving Payment setup intent : ${err.message}`
      }
    }
  }
}
/**
 * Get a payment method object from stripe
 *
 * @param card
 * @param billingDetails
 * @param stripeObject
 * @param paymentIntentToken
 * @returns {Promise<{notification: null, paymentIntent: PaymentIntent}>}
 */
export const createStripePaymentMethod = async ({ card, billingDetails, stripeObject, paymentIntentToken }) => {
  const { paymentIntent, error } = await stripeObject
    .confirmCardPayment(paymentIntentToken.paymentIntent.client_secret, {
      payment_method: {
        card: card,
        billing_details: billingDetails
      }
    })
  if (error) {
    Bugsnag.notify(error)
    return {
      paymentMethod: null,
      notification: {
        type: 'Warning',
        message: `Error Stripe payment method : ${error.message}`
      }
    }
  } else {
    return {
      paymentIntent: paymentIntent,
      notification: null
    }
  }
}
/**
 * Display formatted price
 *
 * @param value
 * @param isoCode
 * @param isInteger  - rounds price to integer
 * @returns {string}
 */
export const formattedPrice = ({ value, isoCode, roundToInteger = false }) => {
  const numberOptions = {
    style: 'currency',
    currency: isoCode
  }
  if (roundToInteger) {
    numberOptions.maximumFractionDigits = 0
  }

  return new Intl.NumberFormat('en-GB', numberOptions).format(value)
}
/**
 * Create customer options object
 * Set Stripe customer object - when we create a subscription or single purchase,
 * Sets :
 * address (this will always have the customer country set, but may have no other data)
 * country code is used in Stripe to determine customer taxes (subscriptions only for now)
 * @param userAddress: user address object || null
 * @param countryIsoCode: two character country code (not optional)
 * @param userEmail: user email address
 * @param userName: firstname + lastname
 *
 * returns Object
 * address,
 */
export const getNewStripeCustomerObject = ({ countryIsoCode, userAddress, userEmail, userName }) => {
  const address = userAddress
    ? {
        city: userAddress.city ?? '',
        line1: userAddress.address_1 ?? '',
        line2: userAddress.address_2 ?? '',
        postal_code: userAddress.zipcode ?? '',
        state: userAddress.state_county ?? '',
        country: countryIsoCode
      }
    : {
        country: countryIsoCode
      }
  return {
    address: address,
    email: userEmail,
    name: userName
  }
}
/**
 * Update user fiscal data - if we have created a new stripe customer,
 * we may need to update the user country code for tax purposes.
 *
 * @params
 * user - current user object
 * countryId
 * currencyId
 *
 * returns object:
 *  notification : null || notification object
 *  isUserUpdated : true || false
 */
export const updateUserBillingData = async ({ user, countryId = '', currencyId = '' }) => {
  // If we have accepted a new user country or currency, then update our user
  const userData = {}
  // If countryId set and user country id has changed then update
  if (countryId && !(user.address && user.address.country_id === countryId)) {
    // Country changed
    userData.address = {
      country_id: countryId
    }
    if (user.address) {
      // Updating existing user address record
      userData.address_id = user.address_id
    }
  }
  if (currencyId && user.currency_id !== currencyId) {
    userData.currency_id = currencyId
  }
  if (isEmpty(userData)) {
    // Nothing changed
    return {
      notification: null,
      isUserUpdated: false
    }
  }
  // Update user record
  try {
    await axios.patch('/user/payment-data', userData)
    return {
      notification: null,
      isUserUpdated: true
    }
  } catch (err) {
    console.log(err)
    return {
      notification: {
        type: 'error',
        message: `Error updating your profile : ${err.message}`
      },
      isUserUpdated: false
    }
  }
}
/**
 * Rounding - use exponential notation to prevent rounding errors
 *
 * @param number          - floating point to be rounded
 * @param decimalPlaces   - decimal places (e.g. 2)
 * @returns {number}      - rounded number
 */
export const roundAccurately = (number, decimalPlaces) => Number(Math.round(number + 'e' + decimalPlaces) + 'e-' + decimalPlaces)
/**
 *
 * @param ticketId
 * @returns object {
 *   'purchasedTicketCount' => $purchasedTicketCount,
 *   'remainingTicketsCount' => $remainingTicketsCount,
 *   'isTicketSoldOut' => $isTicketSoldOut,
 *   'isTicketInSalesWindow' => $isTicketInSalesWindow
 * }
 * ['message' => 'User is already assigned to this ticket']);
 * ['success' => 'Ticket assigned to user successfully']);
 * ['error' => $e]
 */
export const checkTicketAvailability = async ({ ticketId }) => {
  console.log('ticket id', ticketId)
  try {
    const resp = await axios.get(`/event-ticket/check-availability/${ticketId}`)
    return {
      ticketData: resp.data,
      notification: null
    }
  } catch (err) {
    console.log(err)
    return {
      notification: {
        type: 'error',
        message: `Error retrieving ticket data : ${err.message}`
      },
      ticketData: null
    }
  }
}
/**
 * @param ticketId
 * @param userId
 * @returns object
 * { data :
 *    {message: 'User is already assigned to this ticket',
 *     status: false}
 *  OR
 *    {message: 'No seats available',
 *     status: false}
 *  OR
 *     {message: 'Ticket assigned to user successfully',
 *      status: true}
 *   notification : null OR error message
 * }
 */
export const assignUserTicketUnpaid = async ({ ticketId, userId }) => {
  try {
    const resp = await axios.post(`/assign-user-ticket-unpaid/${ticketId}`,
      { user_id: userId })
    return {
      data: resp.data,
      notification: null
    }
  } catch (err) {
    console.log(err)
    return {
      data: null,
      notification: {
        type: 'error',
        message: `Error assigning ticket data : ${err.message}`
      },
      ticketData: null
    }
  }
}

export const confirmCardSetup = async ({ stripe, client_secret, payment_method_id }) => {
  return stripe
    .confirmCardPayment(client_secret, {
      payment_method: payment_method_id
    }).then(function (result) {
      // Handle result.error or result.setupIntent
      if (result.error) {
        Bugsnag.notify(result.error)
        return {
          paymentMethod: null,
          notification: {
            type: 'Warning',
            message: `Error Stripe payment method : ${result.error.message}`
          }
        }
      } else {
        return {
          paymentIntent: result.paymentIntent,
          notification: null
        }
      }
    })
}

/**
 * @param orderSummary
 * @param subtotal
 * @param fees
 * @param total
 * @param userId
 * @param eventId
 * @returns object
 * { data :
 *    {message: 'No seats available',
 *     status: false}
 *  OR
 *     {message: 'Ticket assigned to user successfully',
 *      status: true}
 *   notification : null OR error message
 * }
 */
export const assignMultipleFreeTickets = async ({ orderSummary, subtotal, fees, total, userId, eventId }) => {
  try {
    const resp = await axios.post(`/event-ticket/assign-multiple-free-tickets/${eventId}`,
      {orderSummary: orderSummary, subtotal: subtotal, fees: fees, total: total, userId: userId })
    return {
      data: resp.data,
      notification: null
    }
  } catch (err) {
    console.log(err)
    return {
      data: null,
      notification: {
        type: 'error',
        message: `Error assigning ticket data : ${err.message}`
      }
    }
  }
}

/**
 * @param paymentIntent
 * @param orderSummary
 * @param total
 * @param subtotal
 * @param fees
 * @param currencyId
 * @param currencyIsoCode
 * @param eventId
 * @returns object
 * { data :
 *     {message: 'Ticket assigned to user successfully',
 *      status: true}
 *   notification : null OR error message
 * }
 */
export const purchaseMultiplePaidTickets = async ({ paymentIntent, orderSummary, amountInfo, taxInfo, currencyIsoCode, currencyId, eventId }) => {
  try {
    const resp = await axios.post(`/event-ticket/assign-multiple-paid-tickets/${eventId}`, {
      paymentIntent: paymentIntent,
      orderSummary: orderSummary,
      amountInfo: amountInfo,
      taxInfo: taxInfo,
      currencyIsoCode: currencyIsoCode.toLowerCase(),
      currencyId: currencyId
    })
    if (resp.data.hasOwnProperty('status')) {
      if (resp.data.status) {
        // Payment processed successfully
        return {
          paymentSuccess: true,
          notification: null
        }
      } else {
        // Payment not taken - ticket could not be allocated
        return {
          paymentSuccess: false,
          notification: {
            type: 'Error',
            message: 'There was a problem assigning your ticket, your card has not been charged - please contact support'
          }
        }
      }
    }
    // Looks like we got a 200 response with 'error'
    return {
      paymentSuccess: false,
      cardCouldNotBeProcessed: true,
      notification: null
    }
  } catch (err) {
    Bugsnag.notify(err)
    return {
      paymentSuccess: false,
      notification: {
        type: 'Warning',
        message: `Error processing payment : ${err.message}`
      }
    }
  }
}